<template>
  <div class="tw-relative tw-h-full tw-w-full">
    <!-- Property Paginator Component -->

    <PropertyPaginator
      :properties="selectedProperties"
      :selected-university="selectedUniversity"
      :route-data="routeData"
      :enable-housemate-groups="enableHousemateGroups"
      :enable-shortlist-in-search="enableShortlistInSearch"
      :show-housemate-group-tab="showHousemateGroupTab"
      :is-logged-in-student="isLoggedInStudent"
      @close="closePropertyCard"
      @shortlist-changed="handleShortlistChanged"
    />

    <UniversitySearchFilter
      :universities="universities"
      :selected-university-pin="selectedUniversity"
      @university-selected="handleUniversitySelection"
      @university-hidden="handleUniversityHidden"
    />

    <!-- Loading indicator for routes -->
    <div
      v-if="isLoadingRoutes"
      class="tw-absolute tw-left-4 tw-top-4 tw-z-50 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-white tw-px-3 tw-py-2 tw-shadow-lg"
    >
      <div
        class="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-blue-500 tw-border-t-transparent"
      ></div>
      <span class="tw-text-sm tw-font-medium tw-text-gray-700"
        >Loading routes...</span
      >
    </div>

    <!-- Map Container -->
    <div
      ref="maplibreContainer"
      data-testid="maplibre-container"
      :style="{ height: mapHeight }"
      class="tw-w-full"
    ></div>
  </div>
</template>

<script>
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import { renderPropertyHtml } from './templates/propertyTemplate.js';
import { renderUniversityHtml } from './templates/universityTemplate.js';
import PropertyPaginator from './PropertyPaginator.vue';
import { renderClusterHtml } from './templates/clusterTemplate.js';
import style from '../style/style.json';
import UniversitySearchFilter from '@components/properties-search/components/UniversitySearchFilter.vue';
import { drawGeoJsonRoute } from '@components/properties-search/utils/mapRouteUtils';
import {
  initMap,
  addClusterSourceAndLayers,
  clearMarkers,
  createMapOptions,
  flyTo,
  geographicMidpoint,
  getClusterItems,
  isCoordinateInBounds,
  parseTravelData,
  styleNavigationControl,
  updateClusterSource,
  updateAttributionText,
} from '@components/properties-search/utils/mapUtils';
import { useMapStore } from '@js/stores/map/mapStore';

export default {
  name: 'OpenMap',
  components: { PropertyPaginator, UniversitySearchFilter },
  props: {
    enableHousemateGroups: {
      type: Boolean,
      default: false,
    },
    enableShortlistInSearch: {
      type: Boolean,
      default: false,
    },
    showHousemateGroupTab: {
      type: Boolean,
      default: true,
    },
    isLoggedInStudent: {
      type: Boolean,
      default: false,
    },
    center: {
      type: Array,
      default: () => [-1.4840357452267359, 53.3811595880018], // Sheffield center
    },
    zoom: {
      type: Number,
      default: 11,
    },
    maxBounds: {
      type: Array,
      default: () => [
        [-10.5, 49.5],
        [2.0, 61.0],
      ], // UK and Ireland bounds
    },
    minZoom: {
      type: Number,
      default: 0,
    },
    maxZoom: {
      type: Number,
      default: 22,
    },
    showCardMinZoom: {
      type: Number,
      default: 17,
    },
  },
  data() {
    const mapStore = useMapStore();
    return {
      mapStore,
      maplibreMap: null,
      maplibreMarkerElements: [],
      universityMarkerElements: [],
      models: [],
      properties: [],
      universities: [],
      markers: [],
      selectedProperties: [],
      urlWatcher: null,
      currentUrl: null,
      selectedClusterId: null,
      routeHandles: [],
      routeData: null,
      selectedUniversity: null,
      isLoadingRoutes: false,
      mapHeight: '100vh', // Default fallback
    };
  },
  watch: {
    properties: {
      handler() {
        if (!this.maplibreMap) return;

        if (!this.maplibreMap.isStyleLoaded()) {
          this.maplibreMap.once('load', () => {
            this.addClusterSourceAndLayers();
            this.updateClusterSource();
            setTimeout(() => this.refreshHtmlMarkers(), 100);
          });
          return;
        }

        this.addClusterSourceAndLayers();
        this.updateClusterSource();
        setTimeout(() => this.refreshHtmlMarkers(), 100);
      },
      deep: true,
    },
    universities: {
      handler(newUniversities) {
        if (this.maplibreMap) {
          this.updateUniversitiesFromModels();
        }
        // When universities are loaded, check if there's a selected university in the store
        if (newUniversities.length > 0 && this.mapStore.selectedUniversity) {
          const storeUniversity = this.mapStore.selectedUniversity;
          // Check if the selected university from store matches current selection
          if (
            !this.selectedUniversity ||
            String(storeUniversity.id) !== String(this.selectedUniversity.id)
          ) {
            const foundUniversity = newUniversities.find(
              (u) => String(u.id) === String(storeUniversity.id)
            );
            if (foundUniversity) {
              this.handleUniversitySelection(foundUniversity);
            }
          }
        }
      },
      deep: true,
    },
    'mapStore.selectedUniversity': {
      handler(newUniversity) {
        if (newUniversity) {
          if (
            !this.selectedUniversity ||
            String(newUniversity.id) !== String(this.selectedUniversity.id)
          ) {
            // Wait for universities to be loaded before trying to find the university
            if (this.universities.length === 0) {
              return;
            }

            // Find the matching university in the universities array
            const foundUniversity = this.universities.find(
              (u) => String(u.id) === String(newUniversity.id)
            );

            if (foundUniversity) {
              this.handleUniversitySelection(foundUniversity);
            }
          }
        } else {
          this.handleUniversityHidden();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchModels();
    this.initializeMap();
    this.setupUrlWatcher();
    this.calculateMapHeight();
    window.addEventListener('resize', this.calculateMapHeight);
  },
  beforeUnmount() {
    this.clearAllMarkers();
    if (this.maplibreMap) {
      this.maplibreMap.remove();
    }
    // Clean up URL watcher
    this.cleanupUrlWatcher();
    this.clearRoutes();
    window.removeEventListener('resize', this.calculateMapHeight);

    // clean up listeners when component is destroyed
    if (this.cleanupNavigationControl) {
      this.cleanupNavigationControl();
      this.cleanupNavigationControl = null;
    }
  },
  methods: {
    calculateMapHeight() {
      try {
        // Get the viewport height
        const viewportHeight = window.innerHeight;

        // Find the filter row using the ID
        const filterRow = document.querySelector('#properties_filter');
        let filterHeight = 0;

        if (filterRow) {
          filterHeight = filterRow.offsetHeight;
        }

        // Look for any header elements that might be fixed/sticky
        const header = document.querySelector(
          'header, .header, .navbar, .nav-header'
        );
        let headerHeight = 0;

        if (header) {
          const headerRect = header.getBoundingClientRect();
          // Only count header if it's actually visible and positioned at the top
          if (headerRect.top >= 0 && headerRect.bottom > 0) {
            headerHeight = headerRect.height;
          }
        }

        // Calculate remaining height
        const totalUsedHeight = filterHeight + headerHeight;
        const remainingHeight = viewportHeight - totalUsedHeight;

        // Set a minimum height and add some padding
        const minHeight = 300; // Minimum 300px height

        const finalHeight = Math.max(remainingHeight, minHeight);

        this.mapHeight = `${finalHeight}px`;

        // Trigger map resize if map is already initialized
        if (this.maplibreMap) {
          this.$nextTick(() => {
            this.maplibreMap.resize();
          });
        }
      } catch (error) {
        console.error('Error calculating map height:', error);
        // Fallback to viewport height
        this.mapHeight = '100vh';
      }
    },
    clearRoutes() {
      if (this.routeHandles && this.routeHandles.length) {
        this.routeHandles.forEach((r) => r.remove());
        this.routeHandles = [];
      }
    },
    addClusterSourceAndLayers() {
      addClusterSourceAndLayers(this.maplibreMap);
    },
    updateClusterSource() {
      updateClusterSource(this.maplibreMap, this.properties);
    },
    refreshHtmlMarkers() {
      // Clear existing DOM markers
      this.clearMarkers();
      if (!this.maplibreMap) return;
      const bounds = this.maplibreMap.getBounds();
      // Query rendered cluster features from the map (exact positions)
      const clusterFeatures = this.maplibreMap.queryRenderedFeatures({
        layers: ['clusters'],
      });
      const unclusteredFeatures = this.maplibreMap.queryRenderedFeatures({
        layers: ['unclustered-point'],
      });
      // Helper to check if coord is inside current bounds
      const inBounds = (coord) => isCoordinateInBounds(coord, bounds);

      // Render clusters as HTML markers
      clusterFeatures.forEach((f) => {
        const coords = f.geometry.coordinates;
        if (!inBounds(coords)) return;
        const clusterId = f.properties.cluster_id;
        const pointCount = f.properties.point_count;
        const currentZoom = this.maplibreMap.getZoom();
        const html = renderClusterHtml(
          pointCount,
          currentZoom,
          this.showCardMinZoom
        );
        const el = document.createElement('div');
        el.innerHTML = html;
        const root = el.firstElementChild || el;

        // Apply is-selected css class to selected cluster
        const clusterNode = root.querySelector('.marker-content') || root;
        if (this.selectedClusterId === clusterId) {
          clusterNode.classList.add('is-selected');
        } else {
          clusterNode.classList.remove('is-selected');
        }

        root.addEventListener('click', (e) => {
          e.stopPropagation();
          // unselect property markers
          this.properties = this.properties.map((p) => ({
            ...p,
            isSelected: false,
          }));

          // Get cluster items to check if properties are at different coordinates
          const clusterSource = this.maplibreMap.getSource('properties');
          getClusterItems(clusterSource, clusterId, pointCount).then(
            (clusterItems) => {
              // Check if all properties have the same coordinates
              const coordinates = clusterItems.map(
                (item) => `${item.longitude},${item.latitude}`
              );
              const uniqueCoordinates = new Set(coordinates);

              // If properties are at different coordinates, zoom in instead of showing property card
              if (uniqueCoordinates.size > 1) {
                // Clear any previous selection since we're just zooming
                this.selectedClusterId = null;
                const middlePoint = geographicMidpoint(clusterItems);

                flyTo(this.maplibreMap, {
                  center: [middlePoint.longitude, middlePoint.latitude],
                  zoomOffset: 2,
                  essential: true,
                });

                const currentZoom = this.maplibreMap.getZoom();

                // If already zoomed in enough, always show property cards
                if (currentZoom >= this.showCardMinZoom) {
                  // Set selectedClusterId only when showing property cards
                  this.selectedClusterId = clusterId;
                  this.showPropertyCard(clusterItems);
                  setTimeout(() => this.refreshHtmlMarkers(), 100);
                  return;
                }
                return; // Exit early, don't process cluster data
              }

              // All properties are at the same coordinate - show property card
              // Set selectedClusterId when showing property cards
              this.selectedClusterId = clusterId;
              this.showPropertyCard(clusterItems);
              setTimeout(() => this.refreshHtmlMarkers(), 100);
            }
          );

          this.maplibreMap
            .getSource('properties')
            .getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (err) return;
              flyTo(this.maplibreMap, {
                center: coords,
                essential: true,
              });
            });
        });
        // Make sure the element is clickable
        root.style.cursor = 'pointer';
        const marker = new maplibregl.Marker({ element: root })
          .setLngLat(coords)
          .addTo(this.maplibreMap);
        this.maplibreMarkerElements.push(marker);
      });

      // Render unclustered points using property template
      unclusteredFeatures.forEach((f) => {
        const coords = f.geometry.coordinates;
        if (!inBounds(coords)) return;
        const properties = f.properties || {};

        // Parse travelData if it's a JSON string
        const travelData = parseTravelData(properties.travelData);

        const markerData = {
          longitude: coords[0],
          latitude: coords[1],
          id: properties.id,
          price: properties.price,
          name: properties.name,
          typeString: properties.typeString,
          advertType: properties.advertType,
          images: properties.images,
          roomsAvailableCount: properties.roomsAvailableCount,
          numberOfBathRooms: properties.numberOfBathRooms,
          spareRoomsAvailableCount: properties.spareRoomsAvailableCount,
          travelData: travelData,
          url: properties.url,
          isSelected: Boolean(
            this.properties.find((p) => p.id === properties.id && p.isSelected)
          ),
          isShortlisted: properties.isShortlisted,
          featured: properties.featured,
        };
        const el = this.createPropertyMarkerElement(markerData);
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat(coords)
          .addTo(this.maplibreMap);
        this.maplibreMarkerElements.push(marker);
      });
    },
    fetchModels() {
      const scope = this;

      // Use the property search endpoint; map reloads return MapDataResource.
      // The URL already has the active map variant (map_view / split_view / grid_view).
      const url = new URL(window.location.href);
      // Limits the response to the Json version.
      url.searchParams.set('reload', 'true');

      axios
        .get(url.pathname + url.search, {
          data: {},
        })
        .then(function (response) {
          if (Object.keys(response.data).length === 0) {
            scope.properties = [];
            scope.universities = [];
            return;
          }

          // Update data
          scope.properties = response.data.properties || [];

          // Preserve the isSelected state when updating properties
          if (scope.selectedProperties.length > 0) {
            scope.properties = scope.properties.map((p) => ({
              ...p,
              isSelected: scope.selectedProperties.some((sp) => sp.id === p.id),
            }));
          }
          // Preserve the isSelected state when updating universities
          const selectedUniversityId =
            scope.selectedUniversity?.id ||
            scope.mapStore.selectedUniversity?.id ||
            null;
          scope.universities = (response.data.universities || []).map((u) => ({
            ...u,
            isSelected:
              selectedUniversityId !== null &&
              String(u.id) === String(selectedUniversityId),
          }));
        })
        .catch(function (error) {
          console.error('Error fetching models:', error);
          // Reset data on error
          scope.properties = [];
          scope.universities = [];
        });
    },
    initializeMap() {
      if (this.maplibreMap) {
        return; // Map already initialized
      }

      const mapOptions = createMapOptions({
        container: this.$refs.maplibreContainer,
        style: style,
        center: this.center,
        zoom: this.zoom,
        minZoom: this.minZoom,
        maxZoom: this.maxZoom,
        maxBounds: this.maxBounds,
      });

      try {
        this.maplibreMap = initMap(mapOptions);

        // Apply custom styling to the navigation control after it's added
        this.cleanupNavigationControl = styleNavigationControl(
          this.$refs.maplibreContainer
        );

        // Wait for map to load
        this.maplibreMap.on('load', () => {
          // Replace attribution text with OSM only
          updateAttributionText(this.$refs);

          this.addClusterSourceAndLayers();
          this.updateClusterSource();
          this.refreshHtmlMarkers();
          this.maplibreMap.on('move', this.refreshHtmlMarkers);

          this.$emit('map-loaded', this.maplibreMap);
        });

        this.maplibreMap.on('idle', () => {
          if (!this.maplibreMap.isStyleLoaded()) return;
        });

        this.maplibreMap.on('error', (e) => {
          console.error('Map error:', e);
        });
      } catch (error) {
        console.error('Error creating map:', error);
      }
    },
    createPropertyMarkerElement(property) {
      const el = document.createElement('div');
      el.className = 'property-marker';
      el.style.cursor = 'pointer';
      el.innerHTML = renderPropertyHtml({ property });
      // Apply is-selected css class to selected property
      const contentNode = el.querySelector('.marker-content') || el;
      if (property.isSelected) {
        contentNode.classList.add('is-selected');
      } else {
        contentNode.classList.remove('is-selected');
      }
      el.addEventListener('click', () => {
        this.selectedClusterId = null;
        this.properties = this.properties.map((p) => ({
          ...p,
          isSelected: p.id === property.id,
        }));
        this.showPropertyCard(property);
        this.$emit('marker-click', property);
      });
      el.addEventListener('mouseenter', () => {
        const content = el.querySelector('.marker-content');
        if (content) {
          content.style.transform = 'translateY(-2px)';
          content.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
        }
      });
      el.addEventListener('mouseleave', () => {
        const content = el.querySelector('.marker-content');
        if (content) {
          content.style.transform = 'translateY(0)';
          content.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
        }
      });
      return el;
    },
    updateUniversitiesFromModels() {
      this.clearUniversityMarkers();

      this.universities.forEach((university, index) => {
        try {
          const el = document.createElement('div');
          el.className = 'university-marker';
          el.style.cursor = 'pointer';
          el.innerHTML = renderUniversityHtml({ marker: university });

          const contentNode = el.querySelector('.marker-content') || el;
          if (university.isSelected) {
            contentNode.classList.add('is-selected');
          } else {
            contentNode.classList.remove('is-selected');
          }

          el.addEventListener('click', () => {
            this.handleUniversitySelection(university);
          });

          const markerInstance = new maplibregl.Marker({
            element: el,
          })
            .setLngLat([university.longitude, university.latitude])
            .addTo(this.maplibreMap);
          this.universityMarkerElements.push(markerInstance);
        } catch (error) {
          console.error(`Error creating marker ${index}:`, error);
        }
      });
    },
    showPropertyCard(markers) {
      let middlePoint = { longitude: 0, latitude: 0 };
      if (markers.id === undefined) {
        middlePoint = geographicMidpoint(markers);
      } else {
        middlePoint.longitude = markers.longitude;
        middlePoint.latitude = markers.latitude;
      }

      this.selectedProperties =
        markers.constructor !== Array ? [markers] : markers;
      // Set attribution control to compact variant

      // This hides the full length of the attribution text when the property card is opened, matching the drag behaviour of Maplibre.
      const attributionControl = this.$refs.maplibreContainer.querySelector(
        '.maplibregl-ctrl-attrib'
      );
      if (attributionControl) {
        attributionControl.className =
          'maplibregl-ctrl maplibregl-ctrl-attrib maplibregl-compact';
      }

      // Center the map on the clicked marker
      if (this.maplibreMap) {
        flyTo(this.maplibreMap, {
          center: [middlePoint.longitude, middlePoint.latitude],
          essential: true,
        });
      }

      // If a university is selected, fetch routes for the opened property card
      if (this.selectedUniversity) {
        this.fetchAndDrawRoutes();
      }
    },
    closePropertyCard() {
      this.selectedProperties = [];
      this.clearRoutes(); // Clear routes when property card is closed
      this.selectedClusterId = null;
      // Clear property selection state
      this.properties = this.properties.map((p) => ({
        ...p,
        isSelected: false,
      }));
    },
    handleShortlistChanged(property) {
      const isShortlisted = !property.isShortlisted;

      // Update the property's shortlist state in the properties array
      const propertyIndex = this.properties.findIndex(
        (p) => p.id === property.id
      );

      if (propertyIndex !== -1) {
        this.properties[propertyIndex].isShortlisted = isShortlisted;

        // Refresh the map markers to reflect the updated shortlist state
        this.refreshHtmlMarkers();
      }

      this.$emit('shortlist-changed', {
        ...property,
        isShortlisted,
      });
    },
    clearMarkers() {
      clearMarkers(this.maplibreMarkerElements);
    },
    clearUniversityMarkers() {
      clearMarkers(this.universityMarkerElements);
    },
    clearAllMarkers() {
      this.clearMarkers();
      this.clearUniversityMarkers();
    },
    styleNavigationControl() {
      styleNavigationControl(this.$refs.maplibreContainer);
    },
    setupUrlWatcher() {
      // Store current URL
      this.currentUrl = window.location.href;

      // Set up manual URL watching for non-Vue Router scenarios
      this.urlWatcher = setInterval(() => {
        const newUrl = window.location.href;
        if (newUrl !== this.currentUrl) {
          this.currentUrl = newUrl;
          this.fetchModels();
        }
      }, 100); // Check every 100ms
    },
    cleanupUrlWatcher() {
      if (this.urlWatcher) {
        clearInterval(this.urlWatcher);
        this.urlWatcher = null;
      }
    },
    handleUniversitySelection(university) {
      this.selectedUniversity = university;
      // Update map store
      this.mapStore.setSelectedUniversity(university);

      this.universities = this.universities.map((u) => ({
        ...u,
        isSelected: String(this.selectedUniversity.id) === String(u.id),
      }));

      // Refresh university markers to reflect the isSelected state
      if (this.maplibreMap) {
        this.updateUniversitiesFromModels();
      }
      // If a property card is open, fetch routes immediately
      if (this.selectedProperties.length > 0) {
        this.fetchAndDrawRoutes();
      }
    },
    handleUniversityHidden() {
      this.selectedUniversity = null;
      // Clear from map store
      this.mapStore.setSelectedUniversity(null);
      this.clearRoutes();
      // Clear university selection state
      this.universities = this.universities.map((u) => ({
        ...u,
        isSelected: false,
      }));
      // Refresh university markers to reflect the cleared selection
      if (this.maplibreMap) {
        this.updateUniversitiesFromModels();
      }
    },
    async fetchAndDrawRoutes() {
      if (
        !this.selectedUniversity ||
        this.selectedProperties.length === 0 ||
        this.isLoadingRoutes
      ) {
        return;
      }

      this.isLoadingRoutes = true;
      this.clearRoutes(); // Clear existing routes

      try {
        // Get the first selected property (or handle multiple properties)
        const property = this.selectedProperties[0];

        // Call the travel routes API
        const response = await axios.post('/api/v2/map-v2/travel-routes', {
          property_id: property.id,
          university_id: this.selectedUniversity.id,
        });

        if (response.data.success && response.data.routes) {
          this.routeData = response.data.routes;
          this.drawRoutes(response.data.routes);
        } else {
          console.warn(' No route data received:', response.data);
        }
      } catch (error) {
        console.error(' Error fetching travel routes:', error);
      } finally {
        this.isLoadingRoutes = false;
      }
    },
    drawRoutes(routes) {
      if (!this.maplibreMap || !routes) {
        return;
      }

      Object.entries(routes).forEach(([routeType, routeData]) => {
        if (routeData.coordinates && routeData.coordinates.length > 0) {
          const geojsonRoute = {
            type: 'Feature',
            properties: {
              routeType: routeType,
              duration: routeData.duration,
              distance: routeData.distance,
            },
            geometry: {
              type: 'LineString',
              coordinates: routeData.coordinates,
            },
          };

          try {
            const routeHandle = drawGeoJsonRoute(
              this.maplibreMap,
              geojsonRoute,
              {
                routeType: routeType,
              }
            );
            if (!this.routeHandles) this.routeHandles = [];
            if (routeHandle) {
              this.routeHandles.push(routeHandle);
            }
          } catch (error) {
            console.error(`Failed to add route ${routeType}:`, error);
          }
        }
      });

      // After drawing routes, fit map bounds to include the route
      try {
        // Gather all coordinates from available routes
        const allCoordinates = Object.values(routes)
          .filter(
            (r) => r && Array.isArray(r.coordinates) && r.coordinates.length > 0
          )
          .flatMap((r) => r.coordinates);

        if (allCoordinates.length > 0) {
          const bounds = allCoordinates.reduce((b, coord) => {
            return b.extend(coord);
          }, new maplibregl.LngLatBounds(allCoordinates[0], allCoordinates[0]));

          this.maplibreMap.fitBounds(bounds, {
            padding: {
              top: 100,
              bottom: 250,
              left: 50,
              right: 50,
            },
          });
        }
      } catch (error) {
        console.error('Failed to fit bounds for routes:', error);
      }
    },
  },
};
</script>

<style scoped>
.map-container {
  @apply tw-h-full tw-w-full;
}

.map {
  @apply tw-min-h-96 tw-h-full tw-w-full;
}

/* Property marker styles */
:deep(.property-marker) {
  @apply tw-cursor-pointer;
}

:deep(.marker-content) {
  @apply tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-full tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-800 tw-shadow-md tw-transition-all tw-duration-200;
}

:deep(.marker-content:hover) {
  @apply tw--translate-y-0.5 tw-shadow-lg;
}

:deep(.house-icon) {
  @apply tw-flex tw-items-center tw-justify-center tw-text-teal-500;
}

:deep(.price) {
  @apply tw-font-semibold tw-text-gray-800;
}

:deep(.marker-content.is-selected) {
  @apply tw-shadow-lg tw-ring-2 tw-ring-teal-500;
  background-color: #0082b8 !important;
  color: white !important;
}

:deep(.marker-content.is-selected .price),
:deep(.marker-content.is-selected .house-icon),
:deep(.marker-content.is-selected .heart-icon) {
  color: white !important;
}
</style>
